import { QueryBody } from "@api/internal/utils/types";

/**
 * Generates the payload for listing API requests based on the page type.
 * @param urlPath - The URL path of the listing page.
 * @param page - The current page number (for section pages).
 * @param size - The number of articles to fetch.
 * @param sections - The sections to filter articles by.
 * @param pageType - The type of the page (e.g., "keyword" or "section").
 * @param offset - The offset for pagination (for keyword pages).
 * @returns The payload object for the listing API request.
 */
export const getListingPayload = ({
  urlPath,
  page,
  size,
  sections,
  pageType,
  offset,
  excludeIds,
}: QueryBody): QueryBody => {
  const defaultPayload: QueryBody = {
    size,
    sections,
    excludeIds,
  };

  switch (pageType) {
    case "keyword":
    case "storyThread":
    case "author":
      return { ...defaultPayload, urlPath, offset };

    case "section":
    default:
      return { ...defaultPayload, page };
  }
};

/**
 * Determines the appropriate listing API endpoint based on the page type.
 * @param pageType - The type of the page (e.g., "keyword" or "section").
 * @returns The API endpoint URL as a string.
 */
export const getListingEndpoint = (pageType?: string): string => {
  switch (pageType) {
    case "keyword":
    case "storyThread":
      return "/_plat/api/v1/articles/tags";

    case "author":
      return "/_plat/api/v1/articles/authors";

    case "section":
    default:
      return "/_plat/api/v1/articles/sections";
  }
};

/**
 * Calculates the offset for the listing API request based on the page number and size.
 * Offset is from which article to start fetching the articles.
 */
export const getListingOffset = ({
  pagePass,
  size,
  offset = 0,
  initialListSize = 10,
}: {
  pagePass: number;
  size: number;
  initialListSize: number;
  offset?: number;
}): number => {
  if (initialListSize < size || initialListSize > size) {
    return offset + initialListSize + (pagePass - 1) * size;
  }

  if (offset <= 0) {
    return pagePass * size;
  }

  return offset + pagePass * size;
};
